import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import MobilePhotoInput from '../../../components/ui/MobilePhotoInput';
import { supabase } from '../../../lib/supabase';
import { activityService } from '../../../services/activityService';
import { useAuth } from '../../../contexts/AuthContext';
import useSubscriptionReadOnly from '../../../hooks/useSubscriptionReadOnly';
import useSiteReadOnly from '../../../hooks/useSiteReadOnly';

const BUCKET = 'task-photos';
const UPLOAD_TIMEOUT_MS = 30000; // 30 second timeout per file

const SitePhotoUploadModal = ({ isOpen, onClose, onSuccess, siteId, siteName }) => {
  const { userProfile } = useAuth();
  const { isReadOnly: subscriptionReadOnly } = useSubscriptionReadOnly();
  const { isReadOnly: siteReadOnly } = useSiteReadOnly(siteId);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const handleFilesSelected = (files) => {
    setError('');
    setSelectedFiles(files);
    const newPreviews = files?.map(f => URL.createObjectURL(f));
    previews?.forEach(url => URL.revokeObjectURL(url));
    setPreviews(newPreviews);
  };

  const handleRemoveFile = (index) => {
    URL.revokeObjectURL(previews?.[index]);
    const newFiles = selectedFiles?.filter((_, i) => i !== index);
    const newPreviews = previews?.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    setPreviews(newPreviews);
  };

  // Wraps a promise with a timeout so uploads don't hang forever
  const withTimeout = (promise, ms, label) => {
    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`Upload timed out after ${ms / 1000}s for ${label}`)), ms)
    );
    return Promise.race([promise, timeout]);
  };

  const handleUpload = async () => {
    if (subscriptionReadOnly) {
      // Defence-in-depth — the parent button is also disabled, but a deep-link
      // or back-nav could land on an already-mounted modal under a newly
      // read-only state.
      setError("Your subscription is read-only. Photo uploads are disabled.");
      return;
    }
    if (siteReadOnly) {
      setError('This site is completed — please reactivate to continue.');
      return;
    }
    if (!selectedFiles?.length) {
      setError('Please select at least one photo.');
      return;
    }
    if (!siteId) {
      setError('No site selected. Please go back and select a site first.');
      return;
    }

    setUploading(true);
    setError('');
    setUploadedCount(0);

    try {
      // Step 1: Verify authentication
      const { data: authData, error: authError } = await supabase?.auth?.getUser();
      if (authError) throw new Error(`Authentication error: ${authError.message}`);
      const user = authData?.user;
      if (!user) throw new Error('You are not signed in. Please sign in and try again.');

      // Step 2: Verify siteId is a valid UUID format
      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidPattern?.test(siteId)) {
        throw new Error(`Invalid site ID format: "${siteId}". Please reload the page and try again.`);
      }

      let successCount = 0;

      for (let i = 0; i < selectedFiles?.length; i++) {
        const file = selectedFiles?.[i];
        const timestamp = Date.now() + i;
        const safeName = file?.name?.replace(/[^a-zA-Z0-9._-]/g, '_') || `photo_${timestamp}.jpg`;
        // Use 'site-direct' prefix so gallery service can identify these as site-level photos
        const filePath = `${user?.id}/site-direct-${siteId}/${timestamp}-${safeName}`;

        // Step 3: Upload to storage with timeout
        const uploadPromise = supabase?.storage
          ?.from(BUCKET)
          ?.upload(filePath, file, { cacheControl: '3600', upsert: false });

        const { error: uploadError } = await withTimeout(
          uploadPromise,
          UPLOAD_TIMEOUT_MS,
          file?.name
        );

        if (uploadError) {
          // Provide specific guidance for common storage errors
          if (uploadError?.message?.includes('row-level security') || uploadError?.statusCode === '403') {
            throw new Error(`Permission denied uploading to storage. Your account may not have upload access. (${uploadError.message})`);
          }
          if (uploadError?.message?.includes('Bucket not found')) {
            throw new Error(`Storage bucket "task-photos" not found. Please contact support.`);
          }
          throw new Error(`Storage upload failed for "${file?.name}": ${uploadError.message}`);
        }

        // Step 4: Insert DB record — task_id is NULL for site-direct uploads
        const { error: insertError } = await supabase
          ?.from('task_photos')
          ?.insert({
            task_id: null,        // site-direct upload — no task linked
            site_id: siteId,
            uploaded_by: user?.id,
            file_path: filePath,
            file_name: file?.name || safeName,
            file_size: file?.size || null,
            mime_type: file?.type || 'image/jpeg',
            caption: caption?.trim() || null,
          });

        if (insertError) {
          // Clean up the uploaded file since DB insert failed
          await supabase?.storage?.from(BUCKET)?.remove([filePath])?.catch(() => {});

          if (insertError?.message?.includes('row-level security') || insertError?.code === '42501') {
            throw new Error(`Permission denied saving photo record. Your role may not have insert access to task_photos. (${insertError.message})`);
          }
          if (insertError?.message?.includes('null value') || insertError?.code === '23502') {
            throw new Error(`Database constraint error: a required field is missing. (${insertError.message})`);
          }
          if (insertError?.message?.includes('foreign key') || insertError?.code === '23503') {
            throw new Error(`Invalid site reference. The site ID "${siteId}" may not exist. (${insertError.message})`);
          }
          throw new Error(`Failed to save photo record for "${file?.name}": ${insertError.message}`);
        }

        successCount++;
        setUploadedCount(successCount);
      }

      // Step 5: Log activity (non-critical for the user — photo is already
      // saved). companyId is required by the activities INSERT RLS policy
      // (`with_check (company_id = get_user_company_id())`); without it the
      // INSERT silently fails with 42501. The service has a profile-lookup
      // fallback as defence in depth, but pass the value here so we don't
      // pay an extra round-trip on the happy path.
      //
      // createActivity returns `{ data, error }` rather than throwing, so we
      // inspect the result instead of relying on a try/catch — that's why
      // the previous silent swallow was invisible: a `try { await … } catch`
      // around a non-throwing service never sees the error. Now we surface
      // it via console.warn without breaking the upload UX.
      try {
        const activityResult = await activityService?.createActivity({
          companyId: userProfile?.company_id,
          siteId,
          activityType: 'photo_evidence_added',
          title: `Site photo${successCount > 1 ? 's' : ''} added`,
          description: `${successCount} photo${successCount > 1 ? 's' : ''} uploaded directly to site${caption ? `: "${caption}"` : ''}`,
          metadata: { photoCount: successCount, caption },
        });
        if (activityResult?.error) {
          console.warn('[SitePhotoUploadModal] activity log failed (non-blocking):', { code: activityResult.error?.code, message: activityResult.error?.message });
        }
      } catch (err) {
        // Defensive — createActivity shouldn't throw, but if it ever does
        // (e.g. network fault before the service's own try/catch), still
        // log without breaking the upload.
        console.warn('[SitePhotoUploadModal] activity log threw unexpectedly (non-blocking):', { code: err?.code, message: err?.message });
      }

      // Step 6: Cleanup and show success
      previews?.forEach(url => URL.revokeObjectURL(url));
      setSuccess(true);
      setSelectedFiles([]);
      setPreviews([]);
      setCaption('');

      // Notify parent so it can show a toast and refresh the recent-photos
      // panel even if the user dismisses the modal before the 1.5s auto-close.
      onSuccess?.(successCount);

      setTimeout(() => {
        setSuccess(false);
        onClose?.();
      }, 1500);

    } catch (err) {
      console.error('[SitePhotoUploadModal] upload photo failed:', { code: err?.code, statusCode: err?.statusCode, message: err?.message });
      // Code-branched user-facing copy. The inner throws above already
      // build detailed strings for known shapes (RLS, bucket-not-found,
      // NOT NULL, FK); preserve those by reading err.message. The branches
      // below catch any additional shapes that bubble up via the storage
      // / DB layer directly.
      let msg;
      if (err?.code === 'PGRST301' || err?.code === '42501') {
        msg = `Permission denied uploading photo: ${err?.message ?? 'unknown error'}`;
      } else if (err?.code === '23502') {
        msg = `Missing required field: ${err?.message ?? 'unknown error'}`;
      } else if (String(err?.statusCode ?? '') === '413' || err?.code === 'PAYLOAD_TOO_LARGE') {
        msg = `File too large: ${err?.message ?? 'reduce file size and try again'}`;
      } else {
        msg = `Upload failed: ${err?.message ?? 'unknown error'}`;
      }
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (uploading) return;
    previews?.forEach(url => URL.revokeObjectURL(url));
    setSelectedFiles([]);
    setPreviews([]);
    setCaption('');
    setError('');
    setSuccess(false);
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
      />
      {/* Modal panel — bottom sheet on mobile, centered on desktop */}
      <div className="relative w-full sm:max-w-lg bg-card rounded-t-2xl sm:rounded-2xl shadow-xl z-10 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-border sticky top-0 bg-card rounded-t-2xl sm:rounded-t-2xl z-10">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
              <Icon name="Camera" size={18} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Add Photo</h2>
              {siteName && (
                <p className="text-xs text-muted-foreground">{siteName}</p>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={uploading}
            className="w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Success state */}
          {success && (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
                <Icon name="CheckCircle" size={36} className="text-emerald-500" />
              </div>
              <p className="text-base font-semibold text-foreground">
                {uploadedCount} photo{uploadedCount !== 1 ? 's' : ''} uploaded!
              </p>
              <p className="text-sm text-muted-foreground text-center">
                Photos have been added to the site gallery.
              </p>
            </div>
          )}

          {!success && (
            <>
              {/* Error — always visible, shows real error message */}
              {error && (
                <div className="flex items-start gap-2 bg-destructive/10 border border-destructive/20 rounded-lg p-3">
                  <Icon name="AlertCircle" size={16} className="text-destructive mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-destructive">Upload failed</p>
                    <p className="text-xs text-destructive/80 mt-0.5 break-words">{error}</p>
                  </div>
                </div>
              )}

              {/* Photo input */}
              <div>
                <p className="text-sm font-medium text-foreground mb-2">
                  Select Photos
                </p>
                <MobilePhotoInput
                  onFilesSelected={handleFilesSelected}
                  multiple
                  disabled={uploading}
                  uploading={uploading}
                  label="Select Photos"
                  hint="Take a photo or choose from your gallery"
                />
              </div>

              {/* Preview thumbnails */}
              {previews?.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">
                    {previews?.length} photo{previews?.length !== 1 ? 's' : ''} selected
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {previews?.map((url, idx) => (
                      <div key={idx} className="relative aspect-square rounded-lg overflow-hidden bg-muted border border-border">
                        <img
                          src={url}
                          alt={`Selected photo ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(idx)}
                          disabled={uploading}
                          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-black/80 transition-colors disabled:opacity-50"
                        >
                          <Icon name="X" size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Caption */}
              <div>
                <label className="text-sm font-medium text-foreground block mb-1.5">
                  Caption <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <textarea
                  value={caption}
                  onChange={e => setCaption(e?.target?.value)}
                  placeholder="Describe what this photo shows…"
                  rows={2}
                  disabled={uploading}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent resize-none disabled:opacity-60"
                />
              </div>

              {/* Upload progress */}
              {uploading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Loader2" size={16} className="animate-spin text-accent" />
                  <span>
                    {uploadedCount === 0
                      ? `Uploading photo 1 of ${selectedFiles?.length}…`
                      : `Uploaded ${uploadedCount} of ${selectedFiles?.length}…`}
                  </span>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-1 pb-2">
                <Button
                  variant="outline"
                  size="md"
                  onClick={handleClose}
                  disabled={uploading}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleUpload}
                  disabled={uploading || selectedFiles?.length === 0 || subscriptionReadOnly || siteReadOnly}
                  className="flex-1"
                >
                  {uploading ? (
                    <>
                      <Icon name="Loader2" size={16} className="animate-spin mr-1.5" />
                      Uploading…
                    </>
                  ) : (
                    <>
                      <Icon name="Upload" size={16} className="mr-1.5" />
                      Upload {selectedFiles?.length > 0 ? `(${selectedFiles?.length})` : ''}
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SitePhotoUploadModal;
